/**
 * The unlocked master key for the signed-in session, kept in IndexedDB so reloads and new tabs
 * stay unlocked until sign-out, which deletes it. The browser stores the key object as
 * non-extractable: page scripts can use it but never read it out.
 */

const DB = 'notaryx';
const STORE = 'session-keys';

function open(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB, 1);
    request.onupgradeneeded = () => request.result.createObjectStore(STORE);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function run<T>(
  mode: IDBTransactionMode,
  action: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  const db = await open();
  try {
    return await new Promise<T>((resolve, reject) => {
      const request = action(db.transaction(STORE, mode).objectStore(STORE));
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  } finally {
    db.close();
  }
}

/** Null when nothing is remembered, or when storage is unavailable (private mode, tests). */
export async function loadSessionKey(userId: string): Promise<CryptoKey | null> {
  try {
    const key = await run<unknown>('readonly', (s) => s.get(userId));
    return key instanceof CryptoKey ? key : null;
  } catch {
    return null;
  }
}

export async function saveSessionKey(userId: string, key: CryptoKey) {
  await run('readwrite', (s) => s.put(key, userId));
}

/** On sign-out: every stored session key goes. */
export async function clearSessionKeys() {
  try {
    await run('readwrite', (s) => s.clear());
  } catch {
    // Nothing stored, or storage unavailable: nothing to forget.
  }
}
